import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiRefreshCw, FiCopy, FiCheck, FiX } from 'react-icons/fi';
import './PasswordGenerator.css';

function PasswordGenerator({ onSelect, onClose }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [type, setType] = useState('random'); // random, smart, memorable, pin
  
  // Random password options
  const [length, setLength] = useState(20);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  
  // Memorable password options
  const [wordCount, setWordCount] = useState(4);
  const [separator, setSeparator] = useState('-');
  const [capitalize, setCapitalize] = useState(false);
  const [fullWords, setFullWords] = useState(true);
  
  // PIN options
  const [pinLength, setPinLength] = useState(4);

  // Word list for memorable passwords
  const wordList = [
    'apple', 'banana', 'cherry', 'delta', 'eagle', 'falcon', 'gamma', 'hotel',
    'india', 'juliet', 'kilo', 'lima', 'mike', 'november', 'oscar', 'papa',
    'quebec', 'romeo', 'sierra', 'tango', 'uniform', 'victor', 'whiskey', 'xray',
    'yankee', 'zulu', 'alpha', 'bravo', 'charlie', 'echo', 'foxtrot', 'golf',
    'monty', 'traigas', 'baldwin', 'beta', 'castle', 'dragon', 'ember', 'frost',
    'garden', 'harbor', 'island', 'jungle', 'keeper', 'lantern', 'marble', 'noble',
    'ocean', 'palace', 'quantum', 'river', 'shadow', 'thunder', 'umbrella', 'valley',
    'winter', 'xenon', 'yellow', 'zenith', 'anchor', 'beacon', 'crystal', 'diamond',
    'emerald', 'forest', 'glacier', 'horizon', 'ivory', 'jasmine', 'kingdom', 'legend',
    'meadow', 'neptune', 'orchid', 'phoenix', 'quartz', 'rainbow', 'silver', 'topaz'
  ];

  const generateRandomPassword = useCallback(() => {
    let charset = '';
    if (includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (includeNumbers) charset += '0123456789';
    if (includeSymbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    if (charset === '') charset = 'abcdefghijklmnopqrstuvwxyz';
    
    let result = '';
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      result += charset[array[i] % charset.length];
    }
    return result;
  }, [length, includeNumbers, includeSymbols, includeUppercase, includeLowercase]);

  const generateSmartPassword = useCallback(() => {
    // Smart password: letters + numbers, no ambiguous characters
    const lowerChars = 'abcdefghjkmnpqrstuvwxyz'; // no i, l, o
    const upperChars = 'ABCDEFGHJKMNPQRSTUVWXYZ'; // no I, L, O
    const numbers = '23456789'; // no 0, 1
    const symbols = '@#$%&*!?';
    
    let result = '';
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);
    
    // Ensure at least one of each type
    result += lowerChars[array[0] % lowerChars.length];
    result += upperChars[array[1] % upperChars.length];
    result += numbers[array[2] % numbers.length];
    result += symbols[array[3] % symbols.length];
    
    const allChars = lowerChars + upperChars + numbers + symbols;
    for (let i = 4; i < length; i++) {
      result += allChars[array[i] % allChars.length];
    }
    
    // Shuffle the result
    const shuffleArray = new Uint32Array(result.length);
    crypto.getRandomValues(shuffleArray);
    const shuffled = result.split('').sort((a, b) => shuffleArray[result.indexOf(a)] - shuffleArray[result.indexOf(b)]).join('');
    
    return shuffled;
  }, [length]);

  const generateMemorablePassword = useCallback(() => {
    const words = [];
    const array = new Uint32Array(wordCount);
    crypto.getRandomValues(array);
    
    for (let i = 0; i < wordCount; i++) {
      let word = wordList[array[i] % wordList.length];
      if (!fullWords) {
        word = word.slice(0, 4);
      }
      if (capitalize) {
        word = word.charAt(0).toUpperCase() + word.slice(1);
      }
      words.push(word);
    }
    
    return words.join(separator);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordCount, separator, capitalize, fullWords]);

  const generatePin = useCallback(() => {
    let result = '';
    const array = new Uint32Array(pinLength);
    crypto.getRandomValues(array);
    for (let i = 0; i < pinLength; i++) {
      result += (array[i] % 10).toString();
    }
    return result;
  }, [pinLength]);

  const generatePassword = useCallback(() => {
    let newPassword;
    switch (type) {
      case 'smart':
        newPassword = generateSmartPassword();
        break;
      case 'memorable':
        newPassword = generateMemorablePassword();
        break;
      case 'pin':
        newPassword = generatePin();
        break;
      default:
        newPassword = generateRandomPassword();
    }
    setPassword(newPassword);
    setCopied(false);
  }, [type, generateRandomPassword, generateSmartPassword, generateMemorablePassword, generatePin]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleUsePassword = () => {
    if (onSelect && password) {
      onSelect(password);
    }
  };

  // Calculate password strength
  const calculateStrength = () => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (password.length >= 16) score += 1;
    if (password.length >= 20) score += 1;
    
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^a-zA-Z0-9]/.test(password)) score += 1;
    
    if (score <= 2) return { score: 1, label: t('passwordGenerator.strength.weak'), color: '#ff4444' };
    if (score <= 4) return { score: 2, label: t('passwordGenerator.strength.fair'), color: '#ffaa00' };
    if (score <= 6) return { score: 3, label: t('passwordGenerator.strength.good'), color: '#88cc00' };
    return { score: 4, label: t('passwordGenerator.strength.strong'), color: '#00cc66' };
  };

  const strength = calculateStrength();

  // Generate initial password
  React.useEffect(() => {
    generatePassword();
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="password-generator-overlay" onClick={onClose}>
      <div className="password-generator-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pg-header">
          <h2>{t('passwordGenerator.title')}</h2>
          <button className="pg-close-btn" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        <div className="pg-content">
          {/* Password Display */}
          <div className="pg-password-display">
            <div className="pg-password-field">
              <input
                type="text"
                value={password}
                readOnly
                className="pg-password-input"
              />
              <div className="pg-password-actions">
                <button
                  className="pg-btn-icon"
                  onClick={generatePassword}
                  title={t('passwordGenerator.regenerate')}
                >
                  <FiRefreshCw size={18} />
                </button>
                <button
                  className={`pg-btn-icon ${copied ? 'copied' : ''}`}
                  onClick={copyToClipboard}
                  title={t('passwordGenerator.copy')}
                >
                  {copied ? <FiCheck size={18} /> : <FiCopy size={18} />}
                </button>
              </div>
            </div>
            
            {/* Strength indicator */}
            {password && (
              <div className="pg-strength">
                <div className="pg-strength-bar">
                  {[1, 2, 3, 4].map((level) => (
                    <div
                      key={level}
                      className={`pg-strength-segment ${level <= strength.score ? 'active' : ''}`}
                      style={{ backgroundColor: level <= strength.score ? strength.color : '#333' }}
                    />
                  ))}
                </div>
                <span className="pg-strength-label" style={{ color: strength.color }}>
                  {strength.label}
                </span>
              </div>
            )}
          </div>

          {/* Password Type Selector */}
          <div className="pg-type-selector">
            <label>{t('passwordGenerator.type')}</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="random">{t('passwordGenerator.types.random')}</option>
              <option value="smart">{t('passwordGenerator.types.smart')}</option>
              <option value="memorable">{t('passwordGenerator.types.memorable')}</option>
              <option value="pin">{t('passwordGenerator.types.pin')}</option>
            </select>
          </div>

          {/* Options based on type */}
          <div className="pg-options">
            {(type === 'random' || type === 'smart') && (
              <>
                <div className="pg-option-row">
                  <label>{t('passwordGenerator.length')}</label>
                  <div className="pg-slider-group">
                    <input
                      type="range"
                      min="8"
                      max="64"
                      value={length}
                      onChange={(e) => setLength(parseInt(e.target.value))}
                    />
                    <input
                      type="number"
                      min="8"
                      max="64"
                      value={length}
                      onChange={(e) => setLength(Math.max(8, Math.min(64, parseInt(e.target.value) || 8)))}
                      className="pg-number-input"
                    />
                  </div>
                </div>

                {type === 'random' && (
                  <>
                    <div className="pg-option-toggle">
                      <span>{t('passwordGenerator.numbers')}</span>
                      <label className="pg-switch">
                        <input
                          type="checkbox"
                          checked={includeNumbers}
                          onChange={(e) => setIncludeNumbers(e.target.checked)}
                        />
                        <span className="pg-switch-slider"></span>
                      </label>
                    </div>
                    <div className="pg-option-toggle">
                      <span>{t('passwordGenerator.symbols')}</span>
                      <label className="pg-switch">
                        <input
                          type="checkbox"
                          checked={includeSymbols}
                          onChange={(e) => setIncludeSymbols(e.target.checked)}
                        />
                        <span className="pg-switch-slider"></span>
                      </label>
                    </div>
                    <div className="pg-option-toggle">
                      <span>{t('passwordGenerator.uppercase')}</span>
                      <label className="pg-switch">
                        <input
                          type="checkbox"
                          checked={includeUppercase}
                          onChange={(e) => setIncludeUppercase(e.target.checked)}
                        />
                        <span className="pg-switch-slider"></span>
                      </label>
                    </div>
                    <div className="pg-option-toggle">
                      <span>{t('passwordGenerator.lowercase')}</span>
                      <label className="pg-switch">
                        <input
                          type="checkbox"
                          checked={includeLowercase}
                          onChange={(e) => setIncludeLowercase(e.target.checked)}
                        />
                        <span className="pg-switch-slider"></span>
                      </label>
                    </div>
                  </>
                )}
              </>
            )}

            {type === 'memorable' && (
              <>
                <div className="pg-option-row">
                  <label>{t('passwordGenerator.words')}</label>
                  <div className="pg-slider-group">
                    <input
                      type="range"
                      min="3"
                      max="8"
                      value={wordCount}
                      onChange={(e) => setWordCount(parseInt(e.target.value))}
                    />
                    <input
                      type="number"
                      min="3"
                      max="8"
                      value={wordCount}
                      onChange={(e) => setWordCount(Math.max(3, Math.min(8, parseInt(e.target.value) || 3)))}
                      className="pg-number-input"
                    />
                  </div>
                </div>
                <div className="pg-option-row">
                  <label>{t('passwordGenerator.separator')}</label>
                  <select value={separator} onChange={(e) => setSeparator(e.target.value)}>
                    <option value="-">{t('passwordGenerator.separators.dashes')}</option>
                    <option value=".">{t('passwordGenerator.separators.dots')}</option>
                    <option value="_">{t('passwordGenerator.separators.underscores')}</option>
                    <option value=" ">{t('passwordGenerator.separators.spaces')}</option>
                    <option value="">{t('passwordGenerator.separators.none')}</option>
                  </select>
                </div>
                <div className="pg-option-toggle">
                  <span>{t('passwordGenerator.capitalize')}</span>
                  <label className="pg-switch">
                    <input
                      type="checkbox"
                      checked={capitalize}
                      onChange={(e) => setCapitalize(e.target.checked)}
                    />
                    <span className="pg-switch-slider"></span>
                  </label>
                </div>
                <div className="pg-option-toggle">
                  <span>{t('passwordGenerator.fullWords')}</span>
                  <label className="pg-switch">
                    <input
                      type="checkbox"
                      checked={fullWords}
                      onChange={(e) => setFullWords(e.target.checked)}
                    />
                    <span className="pg-switch-slider"></span>
                  </label>
                </div>
              </>
            )}

            {type === 'pin' && (
              <div className="pg-option-row">
                <label>{t('passwordGenerator.digits')}</label>
                <div className="pg-slider-group">
                  <input
                    type="range"
                    min="4"
                    max="12"
                    value={pinLength}
                    onChange={(e) => setPinLength(parseInt(e.target.value))}
                  />
                  <input
                    type="number"
                    min="4"
                    max="12"
                    value={pinLength}
                    onChange={(e) => setPinLength(Math.max(4, Math.min(12, parseInt(e.target.value) || 4)))}
                    className="pg-number-input"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="pg-actions">
            <button className="pg-btn-secondary" onClick={copyToClipboard}>
              {copied ? t('passwordGenerator.copied') : t('passwordGenerator.copy')}
            </button>
            {onSelect && (
              <button className="pg-btn-primary" onClick={handleUsePassword}>
                {t('passwordGenerator.usePassword')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PasswordGenerator;
